/**
 * Theory of Sovereign Reflectivity
 * p5.js Initialization Script
 * 
 * This script initializes p5.js visualizations for the theory pages
 * without relying on Parcel's bundling system.
 */

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', () => {
    // Initialize interactive diagrams
    const diagrams = document.querySelectorAll('.interactive-diagram');
    if (diagrams && diagrams.length > 0) {
        diagrams.forEach(diagram => {
            try {
                const type = diagram.getAttribute('data-diagram-type');
                if (type) {
                    initDiagram(diagram, type);
                }
            } catch (err) {
                console.warn(`Failed to initialize diagram: ${err.message}`);
            }
        });
    }

    // Initialize highlight tooltips
    initHighlightTooltips();

    // Initialize reading progress bar
    initReadingProgress();

    // Initialize reading settings
    initReadingSettings();

    // Initialize bookmarks
    initBookmarks();
});

/**
 * Initialize highlight tooltips
 */
function initHighlightTooltips() {
    const highlights = document.querySelectorAll('.highlight');

    if (!highlights.length) return;

    highlights.forEach(highlight => {
        // Create tooltip element
        const tooltip = document.createElement('div');
        tooltip.classList.add('highlight-tooltip');
        tooltip.textContent = highlight.getAttribute('data-note');

        // Add tooltip to highlight
        highlight.appendChild(tooltip);

        // Show tooltip on hover
        highlight.addEventListener('mouseenter', () => {
            tooltip.style.opacity = '1';
            tooltip.style.visibility = 'visible';
        });

        // Hide tooltip on mouse leave
        highlight.addEventListener('mouseleave', () => {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
        });
    });
}

/**
 * Initialize reading progress bar
 */
function initReadingProgress() {
    const progressBar = document.querySelector('.progress-bar');
    if (!progressBar) return;

    window.addEventListener('scroll', () => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;

        const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
        progressBar.style.width = `${scrollPercentage}%`;
    });
}

/**
 * Initialize reading settings
 */
function initReadingSettings() {
    const settingsToggle = document.querySelector('.settings-toggle');
    const settingsPanel = document.querySelector('.settings-panel');
    const fontSizeButtons = document.querySelectorAll('.font-size-btn');
    const fontSizeValue = document.querySelector('.font-size-value');
    const readingModeOptions = document.querySelectorAll('.reading-mode-option input');

    if (!settingsToggle || !settingsPanel) return;

    // Toggle settings panel
    settingsToggle.addEventListener('click', () => {
        settingsPanel.classList.toggle('active');
    });

    // Font size controls
    if (fontSizeButtons.length && fontSizeValue) {
        let fontSize = 100;

        fontSizeButtons.forEach(button => {
            button.addEventListener('click', () => {
                if (button.classList.contains('increase-font')) {
                    fontSize = Math.min(fontSize + 10, 150);
                } else {
                    fontSize = Math.max(fontSize - 10, 70);
                }

                fontSizeValue.textContent = `${fontSize}%`;
                document.querySelector('.chapter-content').style.fontSize = `${fontSize}%`;
            });
        });
    }

    // Reading mode options
    if (readingModeOptions.length) {
        readingModeOptions.forEach(option => {
            option.addEventListener('change', () => {
                const body = document.querySelector('.theory-page');
                body.classList.remove('focus-mode', 'column-mode');

                if (option.value === 'focus') {
                    body.classList.add('focus-mode');
                } else if (option.value === 'column') {
                    body.classList.add('column-mode');
                }
            });
        });
    }
}

/**
 * Initialize bookmarks
 */
function initBookmarks() {
    const bookmarkButton = document.querySelector('.bookmark-button');
    const bookmarkPanel = document.querySelector('.bookmark-panel');
    const bookmarkForm = document.querySelector('.bookmark-form');
    const bookmarkList = document.querySelector('.bookmark-list');

    if (!bookmarkButton || !bookmarkPanel || !bookmarkForm || !bookmarkList) return;

    // Toggle bookmark panel
    bookmarkButton.addEventListener('click', () => {
        bookmarkPanel.classList.toggle('active');
        bookmarkButton.classList.toggle('active');
    });

    // Add bookmark
    bookmarkForm.addEventListener('submit', (e) => {
        e.preventDefault();

        const textarea = bookmarkForm.querySelector('textarea');
        const note = textarea.value.trim();

        if (note) {
            const scrollPosition = window.scrollY;
            const timestamp = new Date().toLocaleTimeString();

            // Create bookmark element
            const bookmark = document.createElement('div');
            bookmark.classList.add('bookmark-item');
            bookmark.innerHTML = `
                <div class="bookmark-header">
                    <span class="bookmark-time">${timestamp}</span>
                    <button class="bookmark-delete">×</button>
                </div>
                <div class="bookmark-note">${note}</div>
            `;

            // Add click event to scroll to bookmark position
            bookmark.addEventListener('click', () => {
                window.scrollTo({
                    top: scrollPosition,
                    behavior: 'smooth'
                });
            });

            // Add click event to delete button
            const deleteButton = bookmark.querySelector('.bookmark-delete');
            deleteButton.addEventListener('click', (e) => {
                e.stopPropagation();
                bookmark.remove();
            });

            // Add bookmark to list
            bookmarkList.appendChild(bookmark);

            // Clear textarea
            textarea.value = '';
        }
    });
}

/**
 * Initialize interactive diagrams based on type
 * @param {HTMLElement} element - The diagram container element
 * @param {string} type - The type of diagram to initialize
 */
function initDiagram(element, type) {
    // Create a canvas element with an ID that matches what the visualization modules expect
    const canvasId = `${type}-canvas`;

    // Clear any existing content
    element.innerHTML = '';

    // Set the element ID to match what the visualization modules expect
    element.id = canvasId;

    // Add UI elements needed for the visualizations
    addVisualizationUI(element, type);

    // Initialize the corresponding visualization
    switch (type) {
        case 'energy-equivalence':
            initSimplifiedEnergyEquivalence(element);
            break;
        case 'vibrational-lensing':
            initSimplifiedVibrationalLensing(element);
            break;
        case 'consciousness-kinetic':
            initSimplifiedConsciousnessKinetic(element);
            break;
        case 'time-dilation':
            initSimplifiedTimeDilation(element);
            break;
        case 'length-contraction':
            initSimplifiedLengthContraction(element);
            break;
        case 'resistance-filter':
            initSimplifiedResistanceFilter(element);
            break;
        default:
            // Default placeholder animation
            initPlaceholderDiagram(element);
    }
}

/**
 * Add UI elements needed for the visualizations
 * @param {HTMLElement} element - The diagram container element
 * @param {string} type - The type of diagram to initialize
 */
function addVisualizationUI(element, type) {
    // Check if controls already exist for this element
    const existingControls = element.parentNode.querySelector('.visualization-controls');
    if (existingControls) {
        console.log('Controls already exist for this visualization, skipping UI creation');
        return;
    }

    // Create a container for UI controls
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'visualization-controls';
    controlsContainer.style.marginTop = '20px';
    controlsContainer.style.textAlign = 'center';

    // Add specific UI elements based on visualization type
    switch (type) {
        case 'energy-equivalence':
            // Add sliders for energy equivalence
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="m-np-slider">Nonphysical Mass (M_np): <span id="m-np-value">1.0</span></label>
                    <input type="range" id="m-np-slider" min="0.1" max="2" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="c-np-slider">Consciousness Current (C_np): <span id="c-np-value">1.0</span></label>
                    <input type="range" id="c-np-slider" min="0.1" max="2" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Energy (E_np): <span id="e-np-value">1.0 units</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'vibrational-lensing':
            // Add sliders for vibrational lensing
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="m-np-slider">Nonphysical Mass (M<sub>np</sub>): <span id="m-np-value">1.0</span></label>
                    <input type="range" id="m-np-slider" min="0.1" max="10" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="c-np-slider">Consciousness Current (C<sub>np</sub>): <span id="c-np-value">1.0</span></label>
                    <input type="range" id="c-np-slider" min="0.1" max="3" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Vibrational Lensing Angle: <span id="theta-np-value">1.00 rad</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'resistance-filter':
            // Add sliders for resistance filter
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="intention-slider">Intention Strength (X): <span id="intention-value">1.0</span></label>
                    <input type="range" id="intention-slider" min="0.1" max="5" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="resistance-slider">Resistance Level (R): <span id="resistance-value">0.50</span></label>
                    <input type="range" id="resistance-slider" min="0" max="1" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Outcome (Y): <span id="outcome-value">0.50</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'consciousness-kinetic':
            // Add sliders for consciousness kinetic
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="physical-slider">Physical Energy (E): <span id="physical-value">1.0</span></label>
                    <input type="range" id="physical-slider" min="0.1" max="5" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="nonphysical-slider">Nonphysical Energy (E<sub>np</sub>): <span id="nonphysical-value">1.0</span></label>
                    <input type="range" id="nonphysical-slider" min="0.1" max="5" step="0.1" value="1.0" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Consciousness-Kinetic Energy: <span id="kinetic-value">1.00 units</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'time-dilation':
            // Add sliders for time dilation
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="resistance-slider">Resistance Level (R): <span id="resistance-value">0.50</span></label>
                    <input type="range" id="resistance-slider" min="0" max="0.99" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="alignment-slider">Alignment Factor (A): <span id="alignment-value">0.50</span></label>
                    <input type="range" id="alignment-slider" min="0" max="0.99" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Time Dilation Factor (γ<sub>np</sub>): <span id="gamma-value">2.00</span></label>
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Subjective Time Experience: <span id="time-experience-value">2.00x longer</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;

        case 'length-contraction':
            // Add sliders for length contraction
            controlsContainer.innerHTML = `
                <div style="margin-bottom: 10px;">
                    <label for="focus-slider">Focus Intensity (F): <span id="focus-value">0.5</span></label>
                    <input type="range" id="focus-slider" min="0" max="0.9" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label for="resonance-slider">Vibrational Resonance (R): <span id="resonance-value">0.5</span></label>
                    <input type="range" id="resonance-slider" min="0" max="1" step="0.01" value="0.5" style="width: 80%; margin: 0 auto; display: block;">
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Focus Factor (γ): <span id="gamma-value">2.0</span></label>
                </div>
                <div style="margin-bottom: 10px;">
                    <label>Contracted Distance: <span id="distance-value">50% of original</span></label>
                </div>
                <div style="margin-top: 15px;">
                    <button id="reset-button" style="margin-right: 10px;">Reset</button>
                    <button id="toggle-comparison">Toggle Comparison</button>
                </div>
            `;
            break;
    }

    // Add the controls container after the visualization
    element.parentNode.insertBefore(controlsContainer, element.nextSibling);

    // Add event listeners to the controls
    setupEventListeners(type);
}

/**
 * Setup event listeners for the visualization controls
 * @param {string} type - The type of visualization
 */
function setupEventListeners(type) {
    switch (type) {
        case 'energy-equivalence':
            setupEnergyEquivalenceListeners();
            break;
        case 'vibrational-lensing':
            setupVibrationalLensingListeners();
            break;
        case 'consciousness-kinetic':
            setupConsciousnessKineticListeners();
            break;
        case 'time-dilation':
            setupTimeDilationListeners();
            break;
        case 'length-contraction':
            setupLengthContractionListeners();
            break;
        case 'resistance-filter':
            setupResistanceFilterListeners();
            break;
    }
}

/**
 * Setup event listeners for energy equivalence visualization
 * @param {Object} p - Optional p5 instance to connect to
 */
function setupEnergyEquivalenceListeners(p) {
    // Get the slider elements
    const mNpSlider = document.getElementById('m-np-slider');
    const cNpSlider = document.getElementById('c-np-slider');
    const resetButton = document.getElementById('reset-button');
    const toggleButton = document.getElementById('toggle-comparison');

    // Find the canvas element and get its sketch instance
    const canvasElement = document.getElementById('energy-equivalence-canvas');
    const sketchInstance = p || (canvasElement ? canvasElement.sketchInstance : null);

    if (mNpSlider) {
        mNpSlider.addEventListener('input', function () {
            const mNp = parseFloat(this.value);
            document.getElementById('m-np-value').textContent = mNp.toFixed(1);
            updateEnergyValue();
        });
    }

    if (cNpSlider) {
        cNpSlider.addEventListener('input', function () {
            const cNp = parseFloat(this.value);
            document.getElementById('c-np-value').textContent = cNp.toFixed(1);
            updateEnergyValue();
        });
    }

    if (resetButton) {
        resetButton.addEventListener('click', function () {
            if (sketchInstance && sketchInstance.resetValues) {
                sketchInstance.resetValues();
            } else {
                if (mNpSlider) {
                    mNpSlider.value = 1.0;
                    document.getElementById('m-np-value').textContent = '1.0';
                }
                if (cNpSlider) {
                    cNpSlider.value = 1.0;
                    document.getElementById('c-np-value').textContent = '1.0';
                }
                updateEnergyValue();
            }
        });
    }

    if (toggleButton) {
        toggleButton.addEventListener('click', function () {
            if (sketchInstance && sketchInstance.toggleComparison) {
                sketchInstance.toggleComparison();
            } else {
                this.textContent = this.textContent === 'Toggle Comparison' ? 'Hide Comparison' : 'Toggle Comparison';
            }
        });
    }

    // Initial update
    updateEnergyValue();
}

/**
 * Update the energy value display for energy equivalence visualization
 */
function updateEnergyValue() {
    const mNpSlider = document.getElementById('m-np-slider');
    const cNpSlider = document.getElementById('c-np-slider');
    const eNpElement = document.getElementById('e-np-value');

    if (mNpSlider && cNpSlider && eNpElement) {
        const mNp = parseFloat(mNpSlider.value);
        const cNp = parseFloat(cNpSlider.value);
        const eNp = mNp * cNp * cNp;
        eNpElement.textContent = eNp.toFixed(2) + ' units';
    }
}

/**
 * Setup event listeners for vibrational lensing visualization
 * @param {Object} p - Optional p5 instance to connect to
 */
function setupVibrationalLensingListeners(p) {
    // Get the slider elements
    const mNpSlider = document.getElementById('m-np-slider');
    const cNpSlider = document.getElementById('c-np-slider');
    const resetButton = document.getElementById('reset-button');
    const toggleButton = document.getElementById('toggle-comparison');
    const thetaNpValue = document.getElementById('theta-np-value');

    // Find the canvas element and get its sketch instance
    const canvasElement = document.getElementById('vibrational-lensing-canvas');
    const sketchInstance = p || (canvasElement ? canvasElement.sketchInstance : null);

    if (mNpSlider) {
        mNpSlider.addEventListener('input', function () {
            const mNp = parseFloat(this.value);
            document.getElementById('m-np-value').textContent = mNp.toFixed(1);
            updateVibrationalLensingValues();

            // Update sketch instance if available
            if (sketchInstance && sketchInstance.updateInitialLight) {
                sketchInstance.updateInitialLight(mNp);
            }
        });
    }

    if (cNpSlider) {
        cNpSlider.addEventListener('input', function () {
            const cNp = parseFloat(this.value);
            document.getElementById('c-np-value').textContent = cNp.toFixed(1);
            updateVibrationalLensingValues();

            // Update sketch instance if available
            if (sketchInstance && sketchInstance.updateVibResonance) {
                sketchInstance.updateVibResonance(cNp);
            }
        });
    }

    if (resetButton) {
        resetButton.addEventListener('click', function () {
            if (sketchInstance && sketchInstance.resetValues) {
                sketchInstance.resetValues();
            } else {
                if (mNpSlider) {
                    mNpSlider.value = 1.0;
                    document.getElementById('m-np-value').textContent = '1.0';
                }
                if (cNpSlider) {
                    cNpSlider.value = 1.0;
                    document.getElementById('c-np-value').textContent = '1.0';
                }
                updateVibrationalLensingValues();
            }
        });
    }

    if (toggleButton) {
        toggleButton.addEventListener('click', function () {
            if (sketchInstance && sketchInstance.toggleComparison) {
                sketchInstance.toggleComparison();
            } else {
                this.textContent = this.textContent === 'Toggle Comparison' ? 'Hide Comparison' : 'Toggle Comparison';
            }
        });
    }

    // Initial update
    updateVibrationalLensingValues();
}

/**
 * Update the vibrational lensing values display
 */
function updateVibrationalLensingValues() {
    const mNpSlider = document.getElementById('m-np-slider');
    const cNpSlider = document.getElementById('c-np-slider');
    const thetaNpElement = document.getElementById('theta-np-value');

    if (mNpSlider && cNpSlider && thetaNpElement) {
        const mNp = parseFloat(mNpSlider.value);
        const cNp = parseFloat(cNpSlider.value);
        const thetaNp = Math.atan(mNp * cNp);
        thetaNpElement.textContent = thetaNp.toFixed(2) + ' rad';
    }
}

/**
 * Setup event listeners for consciousness kinetic visualization
 * @param {Object} p - Optional p5 instance to connect to
 */
function setupConsciousnessKineticListeners(p) {
    // Get the slider elements
    const physicalSlider = document.getElementById('physical-slider');
    const nonphysicalSlider = document.getElementById('nonphysical-slider');
    const resetButton = document.getElementById('reset-button');
    const toggleButton = document.getElementById('toggle-comparison');

    // Find the canvas element and get its sketch instance
    const canvasElement = document.getElementById('consciousness-kinetic-canvas');
    const sketchInstance = p || (canvasElement ? canvasElement.sketchInstance : null);

    if (physicalSlider) {
        physicalSlider.addEventListener('input', function () {
            const physicalValue = parseFloat(this.value);
            document.getElementById('physical-value').textContent = physicalValue.toFixed(1);
            updateKineticValue();

            // Update sketch instance if available
            if (sketchInstance && sketchInstance.updatePhysicalEnergy) {
                sketchInstance.updatePhysicalEnergy(physicalValue);
            }
        });
    }

    if (nonphysicalSlider) {
        nonphysicalSlider.addEventListener('input', function () {
            const nonphysicalValue = parseFloat(this.value);
            document.getElementById('nonphysical-value').textContent = nonphysicalValue.toFixed(1);
            updateKineticValue();

            // Update sketch instance if available
            if (sketchInstance && sketchInstance.updateNonphysicalEnergy) {
                sketchInstance.updateNonphysicalEnergy(nonphysicalValue);
            }
        });
    }

    if (resetButton) {
        resetButton.addEventListener('click', function () {
            if (sketchInstance && sketchInstance.resetValues) {
                sketchInstance.resetValues();
            } else {
                if (physicalSlider) {
                    physicalSlider.value = 1.0;
                    document.getElementById('physical-value').textContent = '1.0';
                }
                if (nonphysicalSlider) {
                    nonphysicalSlider.value = 1.0;
                    document.getElementById('nonphysical-value').textContent = '1.0';
                }
                updateKineticValue();
            }
        });
    }

    if (toggleButton) {
        toggleButton.addEventListener('click', function () {
            if (sketchInstance && sketchInstance.toggleComparison) {
                sketchInstance.toggleComparison();
            } else {
                this.textContent = this.textContent === 'Toggle Comparison' ? 'Hide Comparison' : 'Toggle Comparison';
            }
        });
    }

    // Initial update
    updateKineticValue();
}

/**
 * Update the kinetic value display for consciousness kinetic visualization
 */
function updateKineticValue() {
    const physicalSlider = document.getElementById('physical-slider');
    const nonphysicalSlider = document.getElementById('nonphysical-slider');
    const kineticElement = document.getElementById('kinetic-value');

    if (physicalSlider && nonphysicalSlider && kineticElement) {
        const physicalValue = parseFloat(physicalSlider.value);
        const nonphysicalValue = parseFloat(nonphysicalSlider.value);
        const kineticValue = physicalValue * nonphysicalValue;
        kineticElement.textContent = kineticValue.toFixed(2) + ' units';
    }
}

/**
 * Setup event listeners for time dilation visualization
 */
function setupTimeDilationListeners() {
    // Similar to setupEnergyEquivalenceListeners but for time dilation
    // Implementation omitted for brevity
}

/**
 * Setup event listeners for length contraction visualization
 */
function setupLengthContractionListeners() {
    // Similar to setupEnergyEquivalenceListeners but for length contraction
    // Implementation omitted for brevity
}

/**
 * Setup event listeners for resistance filter visualization
 * @param {Object} p - Optional p5 instance to connect to
 */
function setupResistanceFilterListeners(p) {
    // Get the slider elements
    const intentionSlider = document.getElementById('intention-slider');
    const resistanceSlider = document.getElementById('resistance-slider');
    const resetButton = document.getElementById('reset-button');
    const toggleButton = document.getElementById('toggle-comparison');

    // Find the canvas element and get its sketch instance
    const canvasElement = document.getElementById('resistance-filter-canvas');
    const sketchInstance = p || (canvasElement ? canvasElement.sketchInstance : null);

    if (intentionSlider) {
        intentionSlider.addEventListener('input', function () {
            const intentionValue = parseFloat(this.value);
            document.getElementById('intention-value').textContent = intentionValue.toFixed(1);
            updateOutcomeValue();

            // Update sketch instance if available
            if (sketchInstance && sketchInstance.updateInitialEnergy) {
                sketchInstance.updateInitialEnergy(intentionValue);
            }
        });
    }

    if (resistanceSlider) {
        resistanceSlider.addEventListener('input', function () {
            const resistanceValue = parseFloat(this.value);
            document.getElementById('resistance-value').textContent = resistanceValue.toFixed(2);
            updateOutcomeValue();

            // Update sketch instance if available
            if (sketchInstance && sketchInstance.updateResistanceFactor) {
                sketchInstance.updateResistanceFactor(resistanceValue);
            }
        });
    }

    if (resetButton) {
        resetButton.addEventListener('click', function () {
            if (sketchInstance && sketchInstance.resetValues) {
                sketchInstance.resetValues();
            } else {
                if (intentionSlider) {
                    intentionSlider.value = 1.0;
                    document.getElementById('intention-value').textContent = '1.0';
                }
                if (resistanceSlider) {
                    resistanceSlider.value = 0.5;
                    document.getElementById('resistance-value').textContent = '0.50';
                }
                updateOutcomeValue();
            }
        });
    }

    if (toggleButton) {
        toggleButton.addEventListener('click', function () {
            if (sketchInstance && sketchInstance.toggleComparison) {
                sketchInstance.toggleComparison();
            } else {
                this.textContent = this.textContent === 'Toggle Comparison' ? 'Hide Comparison' : 'Toggle Comparison';
            }
        });
    }

    // Initial update
    updateOutcomeValue();
}

/**
 * Update the outcome value display for resistance filter visualization
 */
function updateOutcomeValue() {
    const intentionSlider = document.getElementById('intention-slider');
    const resistanceSlider = document.getElementById('resistance-slider');
    const outcomeElement = document.getElementById('outcome-value');

    if (intentionSlider && resistanceSlider && outcomeElement) {
        const intentionValue = parseFloat(intentionSlider.value);
        const resistanceValue = parseFloat(resistanceSlider.value);
        const outcomeValue = intentionValue * (1 - resistanceValue);
        outcomeElement.textContent = outcomeValue.toFixed(2);
    }
}

/**
 * Initialize a simplified energy equivalence visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedEnergyEquivalence(element) {
    // Create a global sketch instance that can be accessed by event listeners
    let sketchInstance;

    const sketch = (p) => {
        let particles = [];
        let time = 0;
        let mNp = 1.0;
        let cNp = 1.0;
        let showComparison = false;
        let fieldSize = 150;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create particles
            for (let i = 0; i < 50; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(3, 8),
                    speed: p.random(0.5, 2),
                    angle: p.random(p.TWO_PI),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200))
                });
            }

            // Store references to the sliders and buttons for use in the draw loop
            p.mNpSlider = document.getElementById('m-np-slider');
            p.cNpSlider = document.getElementById('c-np-slider');
            p.toggleButton = document.getElementById('toggle-comparison');

            // Setup event listeners
            setupEnergyEquivalenceListeners(p);
        };

        p.draw = () => {
            // Update values from sliders if they exist
            if (p.mNpSlider) mNp = parseFloat(p.mNpSlider.value);
            if (p.cNpSlider) cNp = parseFloat(p.cNpSlider.value);

            // Calculate energy based on current values
            const energy = mNp * cNp * cNp;

            // Update field size based on energy
            fieldSize = 100 + (energy * 50);

            p.background(30, 30, 30);

            // Draw energy field
            p.noFill();
            p.stroke(241, 196, 15, 100);
            p.strokeWeight(2);
            p.ellipse(p.width / 2, p.height / 2, fieldSize + Math.sin(time) * 20);

            // Draw comparison field if enabled
            if (showComparison) {
                p.stroke(46, 204, 113, 100);
                p.ellipse(p.width / 2, p.height / 2, 150 + Math.sin(time) * 20);

                // Draw legend
                p.fill(255);
                p.noStroke();
                p.textAlign(p.LEFT, p.TOP);
                p.textSize(14);
                p.text("Current Energy: " + energy.toFixed(2) + " units", 20, 20);
                p.fill(46, 204, 113);
                p.text("Reference Energy: 1.00 units", 20, 40);
            }

            // Update and draw particles with speed based on energy
            for (let i = 0; i < particles.length; i++) {
                const particleSpeed = particles[i].speed * (0.5 + energy * 0.5);

                // Update particle position
                particles[i].x += Math.cos(particles[i].angle) * particleSpeed;
                particles[i].y += Math.sin(particles[i].angle) * particleSpeed;

                // Bounce off edges
                if (particles[i].x < 0 || particles[i].x > p.width) {
                    particles[i].angle = Math.PI - particles[i].angle;
                }
                if (particles[i].y < 0 || particles[i].y > p.height) {
                    particles[i].angle = -particles[i].angle;
                }

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);
            }

            // Update time
            time += 0.05;
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        };

        // Method to toggle comparison mode
        p.toggleComparison = function () {
            showComparison = !showComparison;
            if (p.toggleButton) {
                p.toggleButton.textContent = showComparison ? 'Hide Comparison' : 'Toggle Comparison';
            }
        };

        // Method to reset values
        p.resetValues = function () {
            mNp = 1.0;
            cNp = 1.0;
            if (p.mNpSlider) {
                p.mNpSlider.value = mNp;
                document.getElementById('m-np-value').textContent = mNp.toFixed(1);
            }
            if (p.cNpSlider) {
                p.cNpSlider.value = cNp;
                document.getElementById('c-np-value').textContent = cNp.toFixed(1);
            }
            updateEnergyValue();
        };
    };

    // Create the sketch and store the instance
    sketchInstance = new p5(sketch);

    // Store the sketch instance on the element for access by event listeners
    element.sketchInstance = sketchInstance;

    return sketchInstance;
}

/**
 * Initialize a simplified vibrational lensing visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedVibrationalLensing(element) {
    // Create a global sketch instance that can be accessed by event listeners
    let sketchInstance;

    const sketch = (p) => {
        let waves = [];
        let particles = [];
        let time = 0;
        let initialLight = 1.0; // Initial light intensity (M_np)
        let vibResonance = 1.0; // Vibrational resonance (C_np)
        let showComparison = false;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create waves
            createWaves();

            // Create particles
            createParticles();

            // Store references to the sliders and buttons for use in the draw loop
            p.mNpSlider = document.getElementById('m-np-slider');
            p.cNpSlider = document.getElementById('c-np-slider');
            p.toggleButton = document.getElementById('toggle-comparison');

            // Setup event listeners
            setupVibrationalLensingListeners(p);
        };

        // Create wave objects for visualization
        function createWaves() {
            waves = [
                { amplitude: 20, frequency: 0.02, phase: 0, color: p.color(231, 76, 60) },
                { amplitude: 20, frequency: 0.05, phase: p.PI / 4, color: p.color(142, 68, 173) },
                { amplitude: 20, frequency: 0.03, phase: p.PI / 2, color: p.color(46, 204, 113) }
            ];
        }

        // Create particles for light visualization
        function createParticles() {
            particles = [];
            const particleCount = 50;

            for (let i = 0; i < particleCount; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(2, 6),
                    speed: p.random(1, 3),
                    angle: p.random(p.TWO_PI),
                    color: p.lerpColor(
                        p.color(231, 76, 60),
                        p.color(46, 204, 113),
                        p.random()
                    )
                });
            }
        }

        p.draw = () => {
            // Update values from sliders if they exist
            if (p.mNpSlider) initialLight = parseFloat(p.mNpSlider.value);
            if (p.cNpSlider) vibResonance = parseFloat(p.cNpSlider.value);

            p.background(30, 30, 30);

            if (showComparison) {
                drawComparisonView();
            } else {
                drawStandardView();
            }

            // Update time
            time += 0.05;
        };

        // Draw standard visualization view
        function drawStandardView() {
            // Draw background grid
            drawGrid();

            // Draw wave visualization
            drawWaves();

            // Draw particles
            drawParticles();

            // Draw lens
            drawLens();

            // Draw labels
            drawLabels();
        }

        // Draw wave visualization
        function drawWaves() {
            const centerY = p.height / 2;

            // Draw baseline
            p.stroke(245, 245, 245, 100);
            p.strokeWeight(1);
            p.line(0, centerY, p.width, centerY);

            // Draw waves
            for (let i = 0; i < waves.length; i++) {
                const wave = waves[i];
                const amplitude = wave.amplitude * (i === 1 ? vibResonance * 2 : 1);

                p.stroke(wave.color);
                p.strokeWeight(2);
                p.noFill();

                p.beginShape();
                for (let x = 0; x < p.width; x += 5) {
                    const y = centerY + Math.sin(x * wave.frequency + time + wave.phase) * amplitude;
                    p.vertex(x, y);
                }
                p.endShape();
            }

            // Draw combined wave
            p.stroke(241, 196, 15);
            p.strokeWeight(3);
            p.noFill();

            p.beginShape();
            for (let x = 0; x < p.width; x += 5) {
                let y = centerY;
                for (let i = 0; i < waves.length; i++) {
                    const wave = waves[i];
                    const amplitude = wave.amplitude * (i === 1 ? vibResonance * 2 : 1) * initialLight;
                    y += Math.sin(x * wave.frequency + time + wave.phase) * amplitude / waves.length;
                }
                p.vertex(x, y);
            }
            p.endShape();
        }

        // Draw and update particles
        function drawParticles() {
            // Check if particles array is initialized
            if (!particles || particles.length === 0) {
                console.warn('Particles array not initialized, recreating particles');
                createParticles();
                return;
            }

            const lensX = p.width / 2;
            const lensWidth = 20;
            const lensHeight = p.height * 0.8;
            const particleCount = Math.floor(50 * initialLight);

            for (let i = 0; i < particles.length; i++) {
                // Skip if particle is undefined
                if (!particles[i]) continue;

                // Update position
                particles[i].x += Math.cos(particles[i].angle) * particles[i].speed;
                particles[i].y += Math.sin(particles[i].angle) * particles[i].speed;

                // Wrap around edges
                if (particles[i].x < 0) particles[i].x = p.width;
                if (particles[i].x > p.width) particles[i].x = 0;
                if (particles[i].y < 0) particles[i].y = p.height;
                if (particles[i].y > p.height) particles[i].y = 0;

                // Apply lens effect
                if (Math.abs(particles[i].x - lensX) < lensWidth / 2 &&
                    particles[i].y > p.height / 2 - lensHeight / 2 &&
                    particles[i].y < p.height / 2 + lensHeight / 2) {

                    // Change direction based on resonance
                    particles[i].angle = p.lerp(particles[i].angle, p.random(p.TWO_PI), vibResonance * 0.1);

                    // Change speed based on resonance
                    particles[i].speed = p.lerp(particles[i].speed, p.random(1, 3) * (1 + vibResonance), 0.1);

                    // Change color based on resonance
                    particles[i].color = p.lerpColor(
                        p.color(231, 76, 60),
                        p.color(46, 204, 113),
                        vibResonance / 3
                    );

                    // Change size based on resonance
                    particles[i].size = p.lerp(particles[i].size, p.random(2, 6) * (1 + vibResonance * 0.5), 0.1);
                }

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);
            }
        }

        // Draw lens
        function drawLens() {
            const lensX = p.width / 2;
            const lensHeight = p.height * 0.8;
            const lensWidth = 20;

            // Draw lens body
            p.noStroke();
            p.fill(142, 68, 173, 100);
            p.rect(lensX - lensWidth / 2, p.height / 2 - lensHeight / 2, lensWidth, lensHeight);

            // Draw lens border
            p.stroke(142, 68, 173);
            p.strokeWeight(2);
            p.noFill();
            p.rect(lensX - lensWidth / 2, p.height / 2 - lensHeight / 2, lensWidth, lensHeight);

            // Draw resonance indicator
            const indicatorHeight = lensHeight * vibResonance / 3;
            p.noStroke();
            p.fill(241, 196, 15, 150);
            p.rect(lensX - lensWidth / 2, p.height / 2 + lensHeight / 2 - indicatorHeight, lensWidth, indicatorHeight);

            // Draw lens label
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(14);
            p.text("Resonance Lens", lensX, p.height / 2 - lensHeight / 2 - 20);
            p.textSize(12);
            p.text(`C_np = ${vibResonance.toFixed(2)}`, lensX, p.height / 2 + lensHeight / 2 + 20);
        }

        // Draw background grid
        function drawGrid() {
            p.stroke(44, 62, 80, 50);
            p.strokeWeight(1);

            const gridSize = 30;
            for (let x = 0; x < p.width; x += gridSize) {
                p.line(x, 0, x, p.height);
            }

            for (let y = 0; y < p.height; y += gridSize) {
                p.line(0, y, p.width, y);
            }
        }

        // Draw comparison view (low vs high resonance)
        function drawComparisonView() {
            // Draw dividing line
            p.stroke(241, 196, 15);
            p.strokeWeight(2);
            p.line(p.width / 2, 0, p.width / 2, p.height);

            // Draw titles
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(16);
            p.text("Low Resonance (C_np = 0.5)", p.width * 0.25, 30);
            p.text("High Resonance (C_np = 2.5)", p.width * 0.75, 30);

            // Draw equations
            p.fill(241, 196, 15);
            p.textSize(20);
            p.text(`θ = arctan(M_np × 0.5)`, p.width * 0.25, 60);
            p.text(`θ = arctan(M_np × 2.5)`, p.width * 0.75, 60);

            // Draw low resonance side (left)
            drawResonanceScenario(p.width * 0.25, 0.5);

            // Draw high resonance side (right)
            drawResonanceScenario(p.width * 0.75, 2.5);

            // Draw explanation
            p.fill(245, 245, 245);
            p.textSize(14);
            p.textAlign(p.CENTER, p.CENTER);
            p.text("Minimal wave bending", p.width * 0.25, p.height - 40);
            p.text("Significant wave bending", p.width * 0.75, p.height - 40);
        }

        // Draw a resonance scenario for the comparison view
        function drawResonanceScenario(centerX, resonanceValue) {
            const centerY = p.height / 2;
            const radius = 100;

            // Draw resonance circle
            p.noFill();
            p.stroke(p.lerpColor(
                p.color(231, 76, 60),
                p.color(46, 204, 113),
                resonanceValue / 3
            ));
            p.strokeWeight(3);
            p.ellipse(centerX, centerY, radius * 2);

            // Draw resonance waves
            for (let i = 0; i < 3; i++) {
                const waveRadius = radius * (0.6 + i * 0.2);
                p.noFill();
                p.stroke(142, 68, 173, 100 - i * 30);
                p.strokeWeight(2);

                p.beginShape();
                for (let angle = 0; angle < p.TWO_PI; angle += 0.1) {
                    const r = waveRadius + Math.sin(angle * 5 + time) * (10 * resonanceValue / 3);
                    const x = centerX + Math.cos(angle) * r;
                    const y = centerY + Math.sin(angle) * r;
                    p.vertex(x, y);
                }
                p.endShape(p.CLOSE);
            }

            // Draw light particles
            const particleCount = 30;
            for (let i = 0; i < particleCount; i++) {
                const angle = i * (p.TWO_PI / particleCount);
                const distance = p.random(0, radius * (1 + resonanceValue / 6));
                const x = centerX + Math.cos(angle + time * resonanceValue / 3) * distance;
                const y = centerY + Math.sin(angle + time * resonanceValue / 3) * distance;

                p.noStroke();
                p.fill(p.lerpColor(
                    p.color(231, 76, 60),
                    p.color(46, 204, 113),
                    resonanceValue / 3
                ));
                p.ellipse(x, y, 4 + resonanceValue);
            }

            // Draw angle indicator
            const thetaNp = Math.atan(initialLight * resonanceValue);
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(14);
            p.text(`M_np = ${initialLight.toFixed(1)}`, centerX, centerY - radius - 40);
            p.text(`θ = ${thetaNp.toFixed(2)} rad`, centerX, centerY + radius + 40);
        }

        // Draw labels
        function drawLabels() {
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);

            // Draw equation at the top
            p.textSize(24);
            p.fill(241, 196, 15);
            p.text(`θ = arctan(M_np × C_np)`, p.width / 2, 30);

            // Draw current values
            p.textSize(16);
            p.fill(245, 245, 245);
            const thetaNp = Math.atan(initialLight * vibResonance);
            p.text(`M_np = ${initialLight.toFixed(1)}   C_np = ${vibResonance.toFixed(2)}   θ = ${thetaNp.toFixed(2)} rad`, p.width / 2, 60);
        }

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        };

        // Method to update initial light (M_np)
        p.updateInitialLight = function (value) {
            initialLight = value;
        };

        // Method to update vibrational resonance (C_np)
        p.updateVibResonance = function (value) {
            vibResonance = value;
        };

        // Method to toggle comparison mode
        p.toggleComparison = function () {
            showComparison = !showComparison;
            if (p.toggleButton) {
                p.toggleButton.textContent = showComparison ? 'Hide Comparison' : 'Toggle Comparison';
            }
        };

        // Method to reset values
        p.resetValues = function () {
            initialLight = 1.0;
            vibResonance = 1.0;
            if (p.mNpSlider) {
                p.mNpSlider.value = initialLight;
                document.getElementById('m-np-value').textContent = initialLight.toFixed(1);
            }
            if (p.cNpSlider) {
                p.cNpSlider.value = vibResonance;
                document.getElementById('c-np-value').textContent = vibResonance.toFixed(1);
            }
            updateVibrationalLensingValues();
        };
    };

    // Create the sketch and store the instance
    sketchInstance = new p5(sketch);

    // Store the sketch instance on the element for access by event listeners
    element.sketchInstance = sketchInstance;

    return sketchInstance;
}

/**
 * Initialize a simplified consciousness kinetic visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedConsciousnessKinetic(element) {
    // Create a global sketch instance that can be accessed by event listeners
    let sketchInstance;

    const sketch = (p) => {
        let particles = [];
        let time = 0;
        let physicalEnergy = 1.0; // Physical energy (E)
        let nonphysicalEnergy = 1.0; // Nonphysical energy (E_np)
        let showComparison = false;
        let fieldSize = 150;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create particles
            createParticles();

            // Store references to the sliders and buttons for use in the draw loop
            p.physicalSlider = document.getElementById('physical-slider');
            p.nonphysicalSlider = document.getElementById('nonphysical-slider');
            p.toggleButton = document.getElementById('toggle-comparison');

            // Setup event listeners
            setupConsciousnessKineticListeners(p);
        };

        // Create particles for visualization
        function createParticles() {
            particles = [];
            const particleCount = 40;

            for (let i = 0; i < particleCount; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(3, 8),
                    speed: p.random(0.5, 2),
                    angle: p.random(p.TWO_PI),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200))
                });
            }
        }

        p.draw = () => {
            // Update values from sliders if they exist
            if (p.physicalSlider) physicalEnergy = parseFloat(p.physicalSlider.value);
            if (p.nonphysicalSlider) nonphysicalEnergy = parseFloat(p.nonphysicalSlider.value);

            // Calculate consciousness-kinetic energy
            const kineticEnergy = physicalEnergy * nonphysicalEnergy;

            // Update field size based on energy
            fieldSize = 100 + (kineticEnergy * 30);

            p.background(30, 30, 30);

            if (showComparison) {
                drawComparisonView();
            } else {
                drawStandardView(kineticEnergy);
            }

            // Update time
            time += 0.05;
        };

        // Draw standard visualization view
        function drawStandardView(kineticEnergy) {
            // Draw background grid
            drawGrid();

            // Draw energy field
            drawEnergyField(kineticEnergy);

            // Update and draw particles
            updateAndDrawParticles(kineticEnergy);

            // Draw labels
            drawLabels(kineticEnergy);
        }

        // Draw energy field
        function drawEnergyField(kineticEnergy) {
            const centerX = p.width / 2;
            const centerY = p.height / 2;

            // Draw physical energy field
            p.noFill();
            p.stroke(231, 76, 60, 100); // Red for physical
            p.strokeWeight(2);
            const physicalRadius = 80 + (physicalEnergy * 20);
            p.ellipse(centerX, centerY, physicalRadius + Math.sin(time) * 10);

            // Draw nonphysical energy field
            p.stroke(46, 204, 113, 100); // Green for nonphysical
            const nonphysicalRadius = 80 + (nonphysicalEnergy * 20);
            p.ellipse(centerX, centerY, nonphysicalRadius + Math.cos(time) * 10);

            // Draw combined consciousness-kinetic field
            p.stroke(241, 196, 15, 150); // Yellow for combined
            p.strokeWeight(3);
            p.ellipse(centerX, centerY, fieldSize + Math.sin(time * 1.5) * 15);

            // Draw field labels
            p.noStroke();
            p.fill(231, 76, 60);
            p.textAlign(p.LEFT, p.TOP);
            p.textSize(12);
            p.text(`Physical Energy: ${physicalEnergy.toFixed(1)}`, 20, 20);

            p.fill(46, 204, 113);
            p.text(`Nonphysical Energy: ${nonphysicalEnergy.toFixed(1)}`, 20, 40);

            p.fill(241, 196, 15);
            p.text(`Consciousness-Kinetic Energy: ${kineticEnergy.toFixed(2)}`, 20, 60);
        }

        // Update and draw particles
        function updateAndDrawParticles(kineticEnergy) {
            const centerX = p.width / 2;
            const centerY = p.height / 2;

            for (let i = 0; i < particles.length; i++) {
                // Calculate direction to center
                const dx = centerX - particles[i].x;
                const dy = centerY - particles[i].y;
                const angle = Math.atan2(dy, dx);
                const distToCenter = Math.sqrt(dx * dx + dy * dy);

                // Adjust particle angle based on energy values
                // Higher nonphysical energy means more attraction to center
                const attractionFactor = 0.01 + (nonphysicalEnergy * 0.02);
                particles[i].angle = p.lerp(particles[i].angle, angle, attractionFactor);

                // Adjust particle speed based on physical energy
                const particleSpeed = particles[i].speed * (0.5 + physicalEnergy * 0.5);

                // Update particle position
                particles[i].x += Math.cos(particles[i].angle) * particleSpeed;
                particles[i].y += Math.sin(particles[i].angle) * particleSpeed;

                // Bounce off edges
                if (particles[i].x < 0 || particles[i].x > p.width) {
                    particles[i].angle = Math.PI - particles[i].angle;
                }
                if (particles[i].y < 0 || particles[i].y > p.height) {
                    particles[i].angle = -particles[i].angle;
                }

                // Adjust particle color based on kinetic energy and distance to center
                const normalizedDist = p.constrain(distToCenter / (p.width / 2), 0, 1);
                const particleColor = p.lerpColor(
                    p.color(241, 196, 15, 200), // Yellow at center
                    p.lerpColor(
                        p.color(231, 76, 60, 150), // Red
                        p.color(46, 204, 113, 150), // Green
                        nonphysicalEnergy / 5
                    ),
                    normalizedDist
                );

                // Draw particle
                p.noStroke();
                p.fill(particleColor);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size * (0.8 + kineticEnergy * 0.1));

                // Draw trail
                p.stroke(particleColor);
                p.strokeWeight(1);
                const trailLength = 5 + (kineticEnergy * 2);
                p.line(
                    particles[i].x,
                    particles[i].y,
                    particles[i].x - Math.cos(particles[i].angle) * particleSpeed * trailLength,
                    particles[i].y - Math.sin(particles[i].angle) * particleSpeed * trailLength
                );
            }
        }

        // Draw background grid
        function drawGrid() {
            p.stroke(44, 62, 80, 30);
            p.strokeWeight(1);

            const gridSize = 30;
            for (let x = 0; x < p.width; x += gridSize) {
                p.line(x, 0, x, p.height);
            }

            for (let y = 0; y < p.height; y += gridSize) {
                p.line(0, y, p.width, y);
            }
        }

        // Draw comparison view
        function drawComparisonView() {
            // Draw dividing line
            p.stroke(241, 196, 15);
            p.strokeWeight(2);
            p.line(p.width / 2, 0, p.width / 2, p.height);

            // Draw titles
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(16);
            p.text("Low Kinetic Energy", p.width * 0.25, 30);
            p.text("High Kinetic Energy", p.width * 0.75, 30);

            // Draw equations
            p.fill(241, 196, 15);
            p.textSize(20);
            p.text(`E_ck = 1.0 × 0.5 = 0.5`, p.width * 0.25, 60);
            p.text(`E_ck = 3.0 × 2.0 = 6.0`, p.width * 0.75, 60);

            // Draw low energy side (left)
            drawEnergyScenario(p.width * 0.25, 1.0, 0.5);

            // Draw high energy side (right)
            drawEnergyScenario(p.width * 0.75, 3.0, 2.0);

            // Draw explanation
            p.fill(245, 245, 245);
            p.textSize(14);
            p.textAlign(p.CENTER, p.CENTER);
            p.text("Minimal particle activity", p.width * 0.25, p.height - 40);
            p.text("High particle activity", p.width * 0.75, p.height - 40);
        }

        // Draw an energy scenario for the comparison view
        function drawEnergyScenario(centerX, phys, nonphys) {
            const centerY = p.height / 2;
            const radius = 80;
            const kineticEnergy = phys * nonphys;

            // Draw energy fields
            p.noFill();

            // Physical energy field
            p.stroke(231, 76, 60, 100);
            p.strokeWeight(2);
            p.ellipse(centerX, centerY, radius + (phys * 20) + Math.sin(time) * 5);

            // Nonphysical energy field
            p.stroke(46, 204, 113, 100);
            p.ellipse(centerX, centerY, radius + (nonphys * 20) + Math.cos(time) * 5);

            // Combined field
            p.stroke(241, 196, 15, 150);
            p.strokeWeight(3);
            p.ellipse(centerX, centerY, radius + (kineticEnergy * 10) + Math.sin(time * 1.5) * 10);

            // Draw particles
            const particleCount = 20;
            for (let i = 0; i < particleCount; i++) {
                const angle = i * (p.TWO_PI / particleCount) + time * (0.2 + kineticEnergy * 0.1);
                const distance = p.random(0, radius * (0.8 + kineticEnergy * 0.1));
                const x = centerX + Math.cos(angle) * distance;
                const y = centerY + Math.sin(angle) * distance;

                // Particle color based on energy
                const particleColor = p.lerpColor(
                    p.color(241, 196, 15, 200),
                    p.lerpColor(
                        p.color(231, 76, 60, 150),
                        p.color(46, 204, 113, 150),
                        nonphys / 5
                    ),
                    distance / radius
                );

                // Draw particle
                p.noStroke();
                p.fill(particleColor);
                p.ellipse(x, y, 3 + kineticEnergy);

                // Draw trail
                if (kineticEnergy > 1) {
                    p.stroke(particleColor);
                    p.strokeWeight(1);
                    p.line(
                        x, y,
                        x - Math.cos(angle) * (3 + kineticEnergy),
                        y - Math.sin(angle) * (3 + kineticEnergy)
                    );
                }
            }

            // Draw energy values
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(14);
            p.text(`E = ${phys.toFixed(1)}`, centerX, centerY - radius - 40);
            p.text(`E_np = ${nonphys.toFixed(1)}`, centerX, centerY - radius - 20);
            p.text(`E_ck = ${kineticEnergy.toFixed(1)}`, centerX, centerY + radius + 30);
        }

        // Draw labels
        function drawLabels(kineticEnergy) {
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);

            // Draw equation at the top
            p.textSize(24);
            p.fill(241, 196, 15);
            p.text(`E_ck = E × E_np`, p.width / 2, 30);

            // Draw current values
            p.textSize(16);
            p.fill(245, 245, 245);
            p.text(`E = ${physicalEnergy.toFixed(1)}   E_np = ${nonphysicalEnergy.toFixed(1)}   E_ck = ${kineticEnergy.toFixed(2)}`, p.width / 2, 60);
        }

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        };

        // Method to update physical energy
        p.updatePhysicalEnergy = function (value) {
            physicalEnergy = value;
        };

        // Method to update nonphysical energy
        p.updateNonphysicalEnergy = function (value) {
            nonphysicalEnergy = value;
        };

        // Method to toggle comparison mode
        p.toggleComparison = function () {
            showComparison = !showComparison;
            if (p.toggleButton) {
                p.toggleButton.textContent = showComparison ? 'Hide Comparison' : 'Toggle Comparison';
            }
        };

        // Method to reset values
        p.resetValues = function () {
            physicalEnergy = 1.0;
            nonphysicalEnergy = 1.0;
            if (p.physicalSlider) {
                p.physicalSlider.value = physicalEnergy;
                document.getElementById('physical-value').textContent = physicalEnergy.toFixed(1);
            }
            if (p.nonphysicalSlider) {
                p.nonphysicalSlider.value = nonphysicalEnergy;
                document.getElementById('nonphysical-value').textContent = nonphysicalEnergy.toFixed(1);
            }
            updateKineticValue();
        };
    };

    // Create the sketch and store the instance
    sketchInstance = new p5(sketch);

    // Store the sketch instance on the element for access by event listeners
    element.sketchInstance = sketchInstance;

    return sketchInstance;
}

/**
 * Initialize a simplified time dilation visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedTimeDilation(element) {
    const sketch = (p) => {
        let time = 0;
        let normalClock, dilatedClock;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            normalClock = {
                x: p.width * 0.3,
                y: p.height / 2,
                radius: 60,
                speed: 1
            };

            dilatedClock = {
                x: p.width * 0.7,
                y: p.height / 2,
                radius: 60,
                speed: 0.5
            };
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw clocks
            drawClock(normalClock, time * normalClock.speed, p.color(231, 76, 60));
            drawClock(dilatedClock, time * dilatedClock.speed, p.color(46, 204, 113));

            // Draw connecting line
            p.stroke(241, 196, 15);
            p.strokeWeight(2);
            p.line(normalClock.x, normalClock.y, dilatedClock.x, dilatedClock.y);

            // Update time
            time += 0.02;
        };

        function drawClock(clock, clockTime, color) {
            // Draw clock face
            p.noFill();
            p.stroke(color);
            p.strokeWeight(3);
            p.ellipse(clock.x, clock.y, clock.radius * 2);

            // Draw hour marks
            for (let i = 0; i < 12; i++) {
                const angle = i * p.TWO_PI / 12;
                const innerRadius = clock.radius * 0.9;
                const outerRadius = clock.radius;
                const x1 = clock.x + Math.cos(angle) * innerRadius;
                const y1 = clock.y + Math.sin(angle) * innerRadius;
                const x2 = clock.x + Math.cos(angle) * outerRadius;
                const y2 = clock.y + Math.sin(angle) * outerRadius;
                p.stroke(200);
                p.strokeWeight(2);
                p.line(x1, y1, x2, y2);
            }

            // Draw second hand
            const secondAngle = (clockTime * 6) % p.TWO_PI;
            const secondX = clock.x + Math.cos(secondAngle - p.HALF_PI) * clock.radius * 0.9;
            const secondY = clock.y + Math.sin(secondAngle - p.HALF_PI) * clock.radius * 0.9;
            p.stroke(color);
            p.strokeWeight(1);
            p.line(clock.x, clock.y, secondX, secondY);

            // Draw minute hand
            const minuteAngle = (clockTime / 60) % p.TWO_PI;
            const minuteX = clock.x + Math.cos(minuteAngle - p.HALF_PI) * clock.radius * 0.7;
            const minuteY = clock.y + Math.sin(minuteAngle - p.HALF_PI) * clock.radius * 0.7;
            p.stroke(200);
            p.strokeWeight(2);
            p.line(clock.x, clock.y, minuteX, minuteY);

            // Draw center dot
            p.fill(200);
            p.noStroke();
            p.ellipse(clock.x, clock.y, 5);
        }

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);

            normalClock.x = p.width * 0.3;
            normalClock.y = p.height / 2;

            dilatedClock.x = p.width * 0.7;
            dilatedClock.y = p.height / 2;
        };
    };

    new p5(sketch);
}

/**
 * Initialize a simplified length contraction visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedLengthContraction(element) {
    const sketch = (p) => {
        let time = 0;
        let objects = [];

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create objects
            objects = [
                {
                    x: p.width * 0.3,
                    y: p.height / 2,
                    width: 100,
                    height: 40,
                    color: p.color(231, 76, 60),
                    contracted: false
                },
                {
                    x: p.width * 0.7,
                    y: p.height / 2,
                    width: 100,
                    height: 40,
                    color: p.color(46, 204, 113),
                    contracted: true
                }
            ];
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Draw objects
            for (let obj of objects) {
                // Calculate contraction
                let displayWidth = obj.width;
                if (obj.contracted) {
                    displayWidth = obj.width * (0.5 + 0.2 * Math.sin(time));
                }

                // Draw object
                p.fill(obj.color);
                p.noStroke();
                p.rect(obj.x - displayWidth / 2, obj.y - obj.height / 2, displayWidth, obj.height);

                // Draw label
                p.fill(255);
                p.textAlign(p.CENTER, p.CENTER);
                p.textSize(14);
                p.text(obj.contracted ? "Contracted" : "Normal", obj.x, obj.y + obj.height + 20);
            }

            // Draw connecting line
            p.stroke(241, 196, 15);
            p.strokeWeight(2);
            p.line(objects[0].x, objects[0].y, objects[1].x, objects[1].y);

            // Update time
            time += 0.05;
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);

            objects[0].x = p.width * 0.3;
            objects[0].y = p.height / 2;

            objects[1].x = p.width * 0.7;
            objects[1].y = p.height / 2;
        };
    };

    new p5(sketch);
}

/**
 * Initialize a simplified resistance filter visualization
 * @param {HTMLElement} element - The container element
 */
function initSimplifiedResistanceFilter(element) {
    // Create a global sketch instance that can be accessed by event listeners
    let sketchInstance;

    const sketch = (p) => {
        let particles = [];
        let filterY;
        let time = 0;
        let initialEnergy = 1.0; // Initial energy (E_i)
        let resistanceFactor = 0.5; // Resistance factor (R_f)
        let finalEnergy = 0.5; // Final energy (E_f) (calculated)
        let showComparison = false;

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            filterY = p.height / 2;

            // Create particles
            createParticles();

            // Store references to the sliders and buttons for use in the draw loop
            p.intentionSlider = document.getElementById('intention-slider');
            p.resistanceSlider = document.getElementById('resistance-slider');
            p.toggleButton = document.getElementById('toggle-comparison');

            // Setup event listeners
            setupResistanceFilterListeners(p);
        };

        // Create particles for visualization
        function createParticles() {
            particles = [];
            const particleCount = 200;

            for (let i = 0; i < particleCount; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(3, 8),
                    speed: p.random(0.5, 2),
                    angle: p.random(p.TWO_PI),
                    color: p.lerpColor(
                        p.color(46, 204, 113), // Green for low resistance
                        p.color(231, 76, 60), // Red for high resistance
                        p.random()
                    ),
                    blocked: false
                });
            }
        }

        p.draw = () => {
            // Update values from sliders if they exist
            if (p.intentionSlider) initialEnergy = parseFloat(p.intentionSlider.value);
            if (p.resistanceSlider) resistanceFactor = parseFloat(p.resistanceSlider.value);

            // Calculate final energy
            finalEnergy = initialEnergy * (1 - resistanceFactor);

            p.background(30, 30, 30);

            if (showComparison) {
                drawComparisonView();
            } else {
                drawStandardView();
            }

            // Update time
            time += 0.05;
        };

        // Draw standard visualization view
        function drawStandardView() {
            // Draw background grid
            drawGrid();

            // Draw filter
            drawFilter();

            // Draw energy particles
            drawEnergyParticles();

            // Draw labels
            drawLabels();
        }

        // Draw filter
        function drawFilter() {
            const filterHeight = 20;
            const filterWidth = p.width * 0.8;
            const filterX = p.width / 2 - filterWidth / 2;

            // Draw filter body
            p.noStroke();
            p.fill(142, 68, 173, 150);
            p.rect(filterX, filterY - filterHeight / 2, filterWidth, filterHeight);

            // Draw filter border
            p.stroke(142, 68, 173);
            p.strokeWeight(2);
            p.noFill();
            p.rect(filterX, filterY - filterHeight / 2, filterWidth, filterHeight);

            // Draw resistance indicator
            const indicatorWidth = filterWidth * resistanceFactor;
            p.noStroke();
            p.fill(231, 76, 60, 150); // Red for resistance
            p.rect(filterX, filterY - filterHeight / 2, indicatorWidth, filterHeight);

            // Draw filter label
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(14);
            p.text("Resistance Filter", p.width / 2, filterY - filterHeight / 2 - 20);
            p.textSize(12);
            p.text(`R_f = ${resistanceFactor.toFixed(2)}`, p.width / 2, filterY + filterHeight / 2 + 20);
        }

        // Draw energy particles
        function drawEnergyParticles() {
            const filterHeight = 20;
            const filterWidth = p.width * 0.8;
            const filterX = p.width / 2 - filterWidth / 2;
            const particleCount = Math.floor(200 * initialEnergy);

            for (let i = 0; i < particles.length; i++) {
                // Skip particles beyond the count determined by initial energy
                if (i >= particleCount) continue;

                // Update position
                if (!particles[i].blocked) {
                    particles[i].x += Math.cos(particles[i].angle) * particles[i].speed;
                    particles[i].y += Math.sin(particles[i].angle) * particles[i].speed;
                } else {
                    // Blocked particles move more slowly and randomly
                    particles[i].x += (Math.random() - 0.5) * 0.5;
                    particles[i].y += (Math.random() - 0.5) * 0.5;
                }

                // Wrap around edges
                if (particles[i].x < 0) particles[i].x = p.width;
                if (particles[i].x > p.width) particles[i].x = 0;
                if (particles[i].y < 0) particles[i].y = p.height;
                if (particles[i].y > p.height) particles[i].y = 0;

                // Check if particle is passing through filter
                if (Math.abs(particles[i].y - filterY) < filterHeight / 2 &&
                    particles[i].x > filterX && particles[i].x < filterX + filterWidth) {

                    // Determine if particle is blocked based on resistance factor
                    if (Math.random() < resistanceFactor) {
                        // Particle is blocked
                        particles[i].blocked = true;
                        particles[i].color = p.color(231, 76, 60); // Red for blocked
                        particles[i].angle = p.random(p.TWO_PI); // Random new direction
                    } else {
                        // Particle passes through
                        particles[i].blocked = false;
                        particles[i].color = p.color(46, 204, 113); // Green for passed
                    }
                }

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);
            }
        }

        // Draw background grid
        function drawGrid() {
            p.stroke(44, 62, 80, 50);
            p.strokeWeight(1);

            const gridSize = 30;
            for (let x = 0; x < p.width; x += gridSize) {
                p.line(x, 0, x, p.height);
            }

            for (let y = 0; y < p.height; y += gridSize) {
                p.line(0, y, p.width, y);
            }
        }

        // Draw comparison view (low vs high resistance)
        function drawComparisonView() {
            // Draw dividing line
            p.stroke(241, 196, 15);
            p.strokeWeight(2);
            p.line(p.width / 2, 0, p.width / 2, p.height);

            // Draw titles
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(16);
            p.text("Low Resistance (R_f = 0.2)", p.width * 0.25, 30);
            p.text("High Resistance (R_f = 0.8)", p.width * 0.75, 30);

            // Draw equations
            p.fill(241, 196, 15);
            p.textSize(20);
            p.text(`E_f = 1.0 × (1 - 0.2) = 0.8`, p.width * 0.25, 60);
            p.text(`E_f = 1.0 × (1 - 0.8) = 0.2`, p.width * 0.75, 60);

            // Draw low resistance side (left)
            drawResistanceScenario(p.width * 0.25, 0.2);

            // Draw high resistance side (right)
            drawResistanceScenario(p.width * 0.75, 0.8);

            // Draw explanation
            p.fill(245, 245, 245);
            p.textSize(14);
            p.textAlign(p.CENTER, p.CENTER);
            p.text("Most energy passes through", p.width * 0.25, p.height - 40);
            p.text("Most energy is blocked", p.width * 0.75, p.height - 40);
        }

        // Draw a resistance scenario for the comparison view
        function drawResistanceScenario(centerX, resistanceValue) {
            const centerY = p.height / 2;
            const boxSize = 120;
            const filterY = centerY;
            const filterHeight = 20;

            // Draw container box
            p.noFill();
            p.stroke(245, 245, 245);
            p.strokeWeight(1);
            p.rect(centerX - boxSize / 2, centerY - boxSize / 2, boxSize, boxSize);

            // Draw filter
            p.noStroke();
            p.fill(p.lerpColor(
                p.color(46, 204, 113), // Green for low resistance
                p.color(231, 76, 60), // Red for high resistance
                resistanceValue
            ));
            p.rect(centerX - boxSize / 2, filterY - filterHeight / 2, boxSize, filterHeight);

            // Draw filter border
            p.stroke(142, 68, 173);
            p.strokeWeight(2);
            p.noFill();
            p.rect(centerX - boxSize / 2, filterY - filterHeight / 2, boxSize, filterHeight);

            // Draw particles
            const particleCount = 50;
            const passedCount = Math.floor(particleCount * (1 - resistanceValue));
            const blockedCount = particleCount - passedCount;

            // Draw particles above filter (input)
            for (let i = 0; i < particleCount; i++) {
                const angle = p.random(p.TWO_PI);
                const distance = p.random(boxSize * 0.4);
                const x = centerX + Math.cos(angle) * distance;
                const y = centerY - boxSize * 0.25 + Math.sin(angle) * distance * 0.5;

                p.noStroke();
                p.fill(241, 196, 15); // Yellow for input
                p.ellipse(x, y, 5);
            }

            // Draw particles below filter (output)
            for (let i = 0; i < passedCount; i++) {
                const angle = p.random(p.TWO_PI);
                const distance = p.random(boxSize * 0.4);
                const x = centerX + Math.cos(angle) * distance;
                const y = centerY + boxSize * 0.25 + Math.sin(angle) * distance * 0.5;

                p.noStroke();
                p.fill(46, 204, 113); // Green for passed
                p.ellipse(x, y, 5);
            }

            // Draw blocked particles at filter
            for (let i = 0; i < blockedCount; i++) {
                const x = centerX - boxSize / 2 + p.random(boxSize);
                const y = filterY + p.random(-filterHeight / 2, filterHeight / 2);

                p.noStroke();
                p.fill(231, 76, 60); // Red for blocked
                p.ellipse(x, y, 5);
            }

            // Draw energy values
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(14);
            p.text(`Input: 1.0`, centerX, centerY - boxSize / 2 - 20);
            p.text(`Output: ${(1 - resistanceValue).toFixed(1)}`, centerX, centerY + boxSize / 2 + 20);

            // Draw resistance label
            p.textSize(12);
            p.text(`Resistance: ${resistanceValue.toFixed(1)}`, centerX, filterY);
        }

        // Draw labels and equation
        function drawLabels() {
            p.noStroke();
            p.fill(245, 245, 245);
            p.textAlign(p.CENTER, p.CENTER);

            // Draw equation at the top
            p.textSize(24);
            p.fill(241, 196, 15);
            p.text(`E_f = E_i × (1 - R_f)`, p.width / 2, 30);

            // Draw current values
            p.textSize(16);
            p.fill(245, 245, 245);
            p.text(`E_i = ${initialEnergy.toFixed(1)}   R_f = ${resistanceFactor.toFixed(2)}   E_f = ${finalEnergy.toFixed(2)}`, p.width / 2, 60);

            // Draw input and output labels
            p.textSize(14);
            p.text("Input Energy", p.width / 2, filterY - 60);
            p.text("Output Energy", p.width / 2, filterY + 60);

            // Draw arrows
            p.stroke(245, 245, 245);
            p.strokeWeight(2);
            p.fill(245, 245, 245);
            drawArrow(p.width / 2, filterY - 40, p.width / 2, filterY - 10);
            drawArrow(p.width / 2, filterY + 10, p.width / 2, filterY + 40);
        }

        // Helper function to draw arrows
        function drawArrow(x1, y1, x2, y2) {
            p.line(x1, y1, x2, y2);

            const angle = p.atan2(y2 - y1, x2 - x1);
            const arrowSize = 10;

            p.push();
            p.translate(x2, y2);
            p.rotate(angle);
            p.triangle(-arrowSize, arrowSize / 2, -arrowSize, -arrowSize / 2, 0, 0);
            p.pop();
        }

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
            filterY = p.height / 2;
        };

        // Method to update initial energy (E_i)
        p.updateInitialEnergy = function (value) {
            initialEnergy = value;
        };

        // Method to update resistance factor (R_f)
        p.updateResistanceFactor = function (value) {
            resistanceFactor = value;
        };

        // Method to toggle comparison mode
        p.toggleComparison = function () {
            showComparison = !showComparison;
            if (p.toggleButton) {
                p.toggleButton.textContent = showComparison ? 'Hide Comparison' : 'Toggle Comparison';
            }
        };

        // Method to reset values
        p.resetValues = function () {
            initialEnergy = 1.0;
            resistanceFactor = 0.5;
            if (p.intentionSlider) {
                p.intentionSlider.value = initialEnergy;
                document.getElementById('intention-value').textContent = initialEnergy.toFixed(1);
            }
            if (p.resistanceSlider) {
                p.resistanceSlider.value = resistanceFactor;
                document.getElementById('resistance-value').textContent = resistanceFactor.toFixed(2);
            }
            updateOutcomeValue();
        };
    };

    // Create the sketch and store the instance
    sketchInstance = new p5(sketch);

    // Store the sketch instance on the element for access by event listeners
    element.sketchInstance = sketchInstance;

    return sketchInstance;
}

/**
 * Initialize a placeholder diagram for other types
 * @param {HTMLElement} element - The container element
 */
function initPlaceholderDiagram(element) {
    const sketch = (p) => {
        let particles = [];

        p.setup = () => {
            const canvas = p.createCanvas(element.offsetWidth, element.offsetHeight);
            canvas.parent(element);

            // Create particles
            for (let i = 0; i < 50; i++) {
                particles.push({
                    x: p.random(p.width),
                    y: p.random(p.height),
                    size: p.random(3, 10),
                    speedX: p.random(-1, 1),
                    speedY: p.random(-1, 1),
                    color: p.color(p.random(100, 255), p.random(100, 255), p.random(100, 255), p.random(150, 200))
                });
            }
        };

        p.draw = () => {
            p.background(30, 30, 30);

            // Update and draw particles
            for (let i = 0; i < particles.length; i++) {
                // Update particle position
                particles[i].x += particles[i].speedX;
                particles[i].y += particles[i].speedY;

                // Bounce off edges
                if (particles[i].x < 0 || particles[i].x > p.width) {
                    particles[i].speedX *= -1;
                }
                if (particles[i].y < 0 || particles[i].y > p.height) {
                    particles[i].speedY *= -1;
                }

                // Draw particle
                p.noStroke();
                p.fill(particles[i].color);
                p.ellipse(particles[i].x, particles[i].y, particles[i].size);
            }
        };

        p.windowResized = () => {
            p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        };
    };

    new p5(sketch);
}
